import { FormError, Labels, PortalBranding, Validations, Visibility } from '@/types';
import { ErrorMessage } from '@/utils/ErrorMessage';

export type RootState = {
    locale: string;
    instanceId: string;
    tenantId: string;
    hasCustomFavIcon: boolean;
    showAnonymousEmailNotification: boolean;
    langs: string[];
    isLoading: boolean;
    errorMessages: ErrorMessage[];
    session?: SessionState;
    customLabels?: CustomLabelsState;
    grievanceForm?: GrievanceFormState;
    grievanceItem?: GrievanceItemState;
    forgotPassword?: ForgotPasswordState;
    resetPassword?: ResetPasswordState;
    confirm?: ConfirmState;
    followUpForm?: FollowUpFormState;
    grievanceHideLogo: boolean;
    portalBranding: PortalBranding;
};

type SelectionList = {
    value: number;
} | null;

export type Document = {
    id: string;
    type: string;
    name: string;
    size: number;
    error?: string;
};

export type FollowUp = {
    id: number;
    author: string;
    date: Date;
    description: string;
    original: boolean;
    documents: Document[];
};

export type portalAccessToken = {
    recordID: number;
    moduleID: string;
    refNo: string;
    exp: number; // Seconds since EPOCH
};

export type GrievanceFormState = {
    description: string;
    type: SelectionList;
    parentCategory: SelectionList;
    category: SelectionList;
    documents: Document[];
    anonymous: boolean;
    title: SelectionList;
    firstName: string;
    lastName: string;
    notification: boolean;
    email: string;
    phone: string;
    addressLine1: string;
    addressLine2: string;
    neighborhood: string;
    sublocality: string;
    city: string;
    state: string;
    country: string;
    postalCode: string;
    sublocality2: string;
    arealevel2: string;
    ageGroup: SelectionList;
    ethnicGroup: SelectionList;
    firstTimeUsed: boolean;
    referer: SelectionList;
    acceptTerms: boolean;
    recaptchaVerifyId: string | null;
    department: SelectionList;
    customTextarea1Toggle: boolean;
    customTextarea1: string | null;
    customTextarea2: string | null;
    customSingle1: SelectionList;
    customSingle2: SelectionList;
    isLoading: boolean;
    isSaving: boolean;
    isUploading: boolean;
    errors: FormError;
    visibility: Visibility;
    validations: Validations;
    labels: Labels;
    projectIdMap: SelectionList;
    customText1: string | null;
    eventDate: Date | null;
};

export type GrievanceItemState = {
    refNo: string;
    date: Date;
    status: {
        text: string;
        color: string;
    };
    type: string | null;
    parentCategory: string | null;
    anonymous: boolean;
    firstName: string;
    lastName: string;
    category: string | null;
    documents: Document[];
    description: string;
    followUps: FollowUp[];
    labels: Labels;

    isLoading: boolean;
};

export type SessionState = {
    portalAccessToken: string;
    recordId: number;
    refNo: string;

    errors: FormError;
    isVerifying: boolean;
};

export enum TermsAndConditionsType {
    URL,
    TEXT,
    TEXT_WITHOUT_CHECKBOX,
}

export type CustomLabelsState = {
    termsAndConditionsUrl: string;
    termsAndConditionsText: string;
    termsAndConditionsType: TermsAndConditionsType;
    privacyPolicyUrl: string;
    portalTitle: string;
    instanceTitle?: string;
    homePageText: string;
    createGrievanceTitle: string;
    createGrievanceText: string;
    footerText: string;
    grievanceFollowUpAuthor: string;
    addFollowUpModalInfo: string;
    accessGrievanceBtn: string;
    accessGrievanceTitle: string;
    accessGrievanceInfo: string;
    grievanceRefNo: string;
    originalGrievance: string;
    grievanceRefNoFormat: string;
    grievanceNumberPlaceholder: string;
    forgotPasswordGrievanceText: string;
    grievanceDescriptionPlaceholder: string;
    grievanceConfirmSuccessText: string;
    grievanceConfirmInfoText: string;
    grievanceConfirmSubmitPasswordSuccessText: string;
    grievanceConfirmFollowUp: string;
    viewInitialGrievance: string;
    viewGrievance: string;
    claimant: string;
    customTermsAndConditions?: string;
    detailsPlaceholder?: string;
    emailNoteText?: string;
};

export type ForgotPasswordState = {
    refNo: string;
    email: string;
    recaptchaVerifyId: string | null;
    isSending: boolean;
    errors: FormError;
    submitted: boolean;
};

export type ResetPasswordState = {
    password: string;
    passwordConfirmation: string;
    passwordStrength: PasswordStrengthState;
    isSending: boolean;
    errors: FormError;
    submitted: boolean;
};

export type ConfirmState = {
    refNo: string;
    passwordCreated: boolean;
    passwordCreationToken: string | null;
    password: string;
    passwordConfirmation: string;
    passwordStrength: PasswordStrengthState;
    isSaving: boolean;
    errors: FormError;
};

export type FollowUpFormState = {
    description: string;
    documents: Document[];
    isSaving: boolean;
    isUploading: boolean;
    errors: FormError;
};

export type PasswordStrengthState = {
    score: number;
    meetsMinimumLengthRequirement: boolean;
    containsUpperCaseLetter: boolean;
    containsLowerCaseLetter: boolean;
    containsAtLeastOneNumber: boolean;
    containsSpecialCharacter: boolean;
    isUncommonPassword: boolean;
};
